import { isAxiosError } from "axios";
import { formatApiErrorDetail } from "../../pay-component-import/utils/apiErrorMessage";

const FEATURE_UNAVAILABLE_MESSAGE =
  "Client pay-run normalisation is not available in this environment. Contact your administrator if you need access.";

const FORBIDDEN_MESSAGE =
  "You do not have permission to use client pay-run normalisation. This tool is limited to Datacom internal users.";

const PAYLOAD_TOO_LARGE_MESSAGE =
  "The PDF is too large. Upload a smaller combined payslip export (maximum 20 MB).";

const UNSUPPORTED_MEDIA_MESSAGE =
  "Upload a PDF file (application/pdf). Scanned images are not supported in v1.";

function isBlobLike(data: unknown): data is Blob {
  if (data === null || typeof data !== "object") {
    return false;
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return true;
  }
  if (Object.prototype.toString.call(data) === "[object Blob]") {
    return true;
  }
  const candidate = data as Blob;
  return (
    typeof candidate.text === "function" ||
    typeof candidate.arrayBuffer === "function"
  );
}

function readBlobAsTextWithFileReader(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read blob"));
    };
    reader.readAsText(blob);
  });
}

async function readBlobAsText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") {
    try {
      return await blob.text();
    } catch {
      // jsdom Blob may lack a working `.text()` — fall back below.
    }
  }
  if (typeof blob.arrayBuffer === "function") {
    try {
      const buffer = await blob.arrayBuffer();
      return new TextDecoder().decode(buffer);
    } catch {
      // jsdom may expose arrayBuffer but not implement it — fall back below.
    }
  }
  return readBlobAsTextWithFileReader(blob);
}

/**
 * When `responseType: "blob"`, Axios puts error JSON in a Blob body — parse before formatting.
 */
export async function resolveAxiosErrorBody(data: unknown): Promise<unknown> {
  if (isBlobLike(data)) {
    try {
      const text = await readBlobAsText(data);
      if (text.trim() === "") {
        return undefined;
      }
      try {
        return JSON.parse(text) as unknown;
      } catch {
        return text;
      }
    } catch {
      return undefined;
    }
  }
  return data;
}

function toClientPayNormalisationErrorFromHttp(
  status: number | undefined,
  data: unknown,
): Error {
  if (status === 403) {
    return new Error(FORBIDDEN_MESSAGE);
  }
  if (status === 503) {
    return new Error(FEATURE_UNAVAILABLE_MESSAGE);
  }
  if (status === 413) {
    return new Error(
      typeof data === "object" && data !== null
        ? formatApiErrorDetail(data, PAYLOAD_TOO_LARGE_MESSAGE)
        : PAYLOAD_TOO_LARGE_MESSAGE,
    );
  }
  if (status === 415) {
    return new Error(
      typeof data === "object" && data !== null
        ? formatApiErrorDetail(data, UNSUPPORTED_MEDIA_MESSAGE)
        : UNSUPPORTED_MEDIA_MESSAGE,
    );
  }
  if (status !== undefined && data !== undefined) {
    return new Error(formatApiErrorDetail(data));
  }

  return new Error("Normalisation failed. Please try again.");
}

/**
 * User-facing message for failed normalisation HTTP calls (#1098).
 */
export function toClientPayNormalisationError(error: unknown): Error {
  if (isAxiosError(error)) {
    if (error.code === "ERR_CANCELED") {
      return new Error("Download cancelled.");
    }
    return toClientPayNormalisationErrorFromHttp(
      error.response?.status,
      error.response?.data,
    );
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error("Normalisation failed. Please try again.");
}

/** Same as {@link toClientPayNormalisationError} but resolves blob error bodies (artifact GET). */
export async function toClientPayNormalisationErrorAsync(
  error: unknown,
): Promise<Error> {
  if (isAxiosError(error)) {
    if (error.code === "ERR_CANCELED") {
      return new Error("Download cancelled.");
    }
    const data = await resolveAxiosErrorBody(error.response?.data);
    return toClientPayNormalisationErrorFromHttp(error.response?.status, data);
  }
  return toClientPayNormalisationError(error);
}
